import 'ol/ol.css';
import { useEffect, useRef, useState } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorTileLayer from 'ol/layer/VectorTile';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import VectorTileSource from 'ol/source/VectorTile';
import MVT from 'ol/format/MVT';
import { Style, Stroke, Fill, Circle as CircleStyle } from 'ol/style';
import { fromLonLat } from 'ol/proj';
import { api } from '../../api/client';
import type { MbtilesEntry } from '../../api/types';
import { ExampleControls } from './Controls';
import { InfoPanel } from '../../components/InfoPanel';
import {
  buildTileJsonUrl,
  classifyFormat,
  defaultBaseUrl,
  fetchTileJson,
  resolveTileUrl,
  withTokenQuery,
  type AccessMode,
} from './shared';

const DEFAULT_VECTOR_STYLE = (() => {
  // 모든 vector_layers를 단색 outline + 점 으로 그리는 디버그용 스타일.
  // mbtiles별 레이어 이름·속성을 모르므로 "타일이 잘 로드되는지" 만 확인.
  const stroke = new Stroke({ color: '#2563eb', width: 1 });
  const fill = new Fill({ color: 'rgba(37, 99, 235, 0.1)' });
  const circle = new CircleStyle({ radius: 2, fill, stroke });
  return new Style({ stroke, fill, image: circle });
})();

export function OpenLayersExamplePage() {
  const [baseUrl, setBaseUrl] = useState<string>(() => defaultBaseUrl());
  const [mbtiles, setMbtiles] = useState<MbtilesEntry[] | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [mode, setMode] = useState<AccessMode>('public');
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mapRef = useRef<Map | null>(null);
  const mapDivRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    api
      .listMbtiles()
      .then((m) => {
        setMbtiles(m);
        if (m.length > 0) setSelectedId(m[0].id);
      })
      .catch((err: Error) => setError(err.message));
  }, []);

  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    mapRef.current = new Map({
      target: mapDivRef.current,
      layers: [],
      view: new View({ center: fromLonLat([127.5, 36.5]), zoom: 6 }),
    });
    return () => {
      mapRef.current?.setTarget(undefined);
      mapRef.current = null;
    };
  }, []);

  // selectedId / mode / token 변경 시 레이어 교체.
  useEffect(() => {
    if (!mapRef.current || !selectedId) return;
    const map = mapRef.current;

    let cancelled = false;
    setError(null);

    const tileJsonUrl = buildTileJsonUrl(baseUrl, selectedId, mode, token);

    fetchTileJson(tileJsonUrl)
      .then((tj) => {
        if (cancelled || !mapRef.current) return;
        // 기존 레이어 제거 후 OSM 배경 위에 mbtiles 레이어를 올린다.
        // OSM tile usage policy 상 프로덕션 사용은 제한 — 본 페이지는 개발 데모.
        map.getLayers().clear();
        map.addLayer(new TileLayer({ source: new OSM() }));

        const rawTilesUrl = tj.tiles[0];
        if (!rawTilesUrl) {
          setError('TileJSON에 tiles[] 가 비었습니다.');
          return;
        }
        const tilesUrl = resolveTileUrl(baseUrl, rawTilesUrl);
        const finalUrl = mode === 'token' ? withTokenQuery(tilesUrl, token) : tilesUrl;
        const kind = classifyFormat(tj.format);

        if (kind === 'raster') {
          map.addLayer(
            new TileLayer({
              source: new XYZ({
                url: finalUrl,
                minZoom: tj.minzoom,
                maxZoom: tj.maxzoom,
                attributions: tj.name,
              }),
            }),
          );
        } else {
          map.addLayer(
            new VectorTileLayer({
              source: new VectorTileSource({
                format: new MVT(),
                url: finalUrl,
                minZoom: tj.minzoom,
                maxZoom: tj.maxzoom,
                attributions: tj.name,
              }),
              style: DEFAULT_VECTOR_STYLE,
            }),
          );
        }

        // bounds로 view 맞춤.
        if (tj.center) {
          map.getView().setCenter(fromLonLat([tj.center[0], tj.center[1]]));
          if (tj.center[2] !== undefined) {
            // center z 가 maxzoom 끝(예: 20)으로 박혀 있으면 화면에 폴리곤 한 개가
            // 가득 차서 데이터가 없어 보인다 — maxzoom 과 OSM 가용 범위(≤19) 한도로 제한.
            map.getView().setZoom(
              Math.min(tj.center[2], tj.maxzoom ?? 19, 18),
            );
          }
        } else if (tj.bounds) {
          const [w, s, e, n] = tj.bounds;
          // bounds 가 좁으면 fit() 이 무한정 줌인하므로 maxzoom 으로 클램프 —
          // 그렇지 않으면 OL/MapLibre 가 source 한계 밖의 z 까지 타일을 요청한다.
          map.getView().fit(
            [...fromLonLat([w, s]), ...fromLonLat([e, n])] as [
              number,
              number,
              number,
              number,
            ],
            { padding: [20, 20, 20, 20], maxZoom: tj.maxzoom },
          );
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, selectedId, mode, token]);

  return (
    <>
      <h2>OpenLayers 예제</h2>
      <InfoPanel
        what={
          <p style={{ margin: 0 }}>
            OpenLayers 의 <code>XYZ</code> source (raster) 또는{' '}
            <code>VectorTile</code>+<code>MVT</code> source (vector) 로 mbtiler 의
            타일을 렌더링합니다. TileJSON 의 <code>format</code> 으로 자동 분기하고,
            배경엔 OSM raster 를 깝니다.
          </p>
        }
        why={
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            <li>
              기존 OpenLayers 기반 GIS 앱에 mbtiler 를 끼워넣을 때 그대로 복사할 수
              있는 코드 패턴.
            </li>
            <li>
              public 인 mbtiles 를 모드 토글로 보거나 read-token 으로 private 을
              열어, 권한 게이트가 의도대로 동작하는지 직접 확인.
            </li>
            <li>
              vector 의 단색 outline 스타일은 mbtiles 별 의미를 모르기 때문 — 실
              사용 시 자체 스타일 정의 필요.
            </li>
          </ul>
        }
      />
      {error && (
        <div className="banner error" data-testid="example-error">
          {error}
        </div>
      )}
      <ExampleControls
        baseUrl={baseUrl}
        onChangeBaseUrl={setBaseUrl}
        mbtiles={mbtiles}
        selectedId={selectedId}
        onSelectId={setSelectedId}
        mode={mode}
        onChangeMode={setMode}
        token={token}
        onChangeToken={setToken}
      />
      <div
        ref={mapDivRef}
        data-testid="example-map"
        style={{ width: '100%', height: 520, borderRadius: 6, overflow: 'hidden' }}
      />
    </>
  );
}
